import { Truck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatPhp } from '@features/quotations/utils/pricing';
import {
  parseFreightCharge,
  type FreightCharge,
  type QuotationDraft,
} from '@features/quotations/types/quotation.types';

/**
 * Read-only freight summary for Submitted / Sent quotations. Renders directly under
 * the customer ReadOnlySummaryBand and matches its visual language (same container
 * styling, same label:value grid, same text size) so the two blocks read as one
 * coherent band.
 *
 * Source of truth: `freightSnapshotJson` — the FreightCharge discriminated union
 * captured at submit time. Type-level invariants guarantee MetroManilaFlat carries
 * a destination, so bug QD-202605-00207 (MM ticked + null destination) is
 * unrepresentable. Phase-4 backfill brought every historical Submitted row onto
 * this shape, so no fallback path is needed.
 */
export function FreightChargeSummary({ draft }: { draft: QuotationDraft }) {
  if (draft.waivedFreight === '1') {
    return <FreightBanner tone="amber" text="Waived — no FREIGHT line on the submitted Estimate." />;
  }

  const parsed = parseFreightCharge(draft.freightSnapshotJson);

  if (parsed) {
    return <FreightChargeDetails charge={parsed} />;
  }

  return <FreightBanner tone="muted" text="No freight charge on this quotation." />;
}

function FreightChargeDetails({ charge }: { charge: FreightCharge }) {
  if (charge.kind === 'Waived') {
    return <FreightBanner tone="amber" text="Waived — no FREIGHT line on the submitted Estimate." />;
  }
  if (charge.kind === 'None') {
    return <FreightBanner tone="muted" text="No freight charge on this quotation." />;
  }

  const truckLabel = truckTypeLabel(charge.truck);
  const truckClassValue =
    charge.numTrucks > 0 ? `${charge.numTrucks} × ${truckLabel}` : truckLabel;
  const destinationDisplay =
    charge.destination.cityName +
    (charge.destination.name ? ` (${charge.destination.name})` : '');

  return (
    <div className="border-y bg-muted/30 px-4 py-3">
      <div className="mb-2 flex items-center gap-2 text-sm">
        <Truck className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="font-semibold uppercase tracking-wide text-muted-foreground text-xs">
          Freight & Delivery
        </span>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm md:grid-cols-3 lg:grid-cols-4">
        <ReadOnlyPair label="Destination" value={destinationDisplay} />
        <ReadOnlyPair label="Truck class" value={truckClassValue} />
        <ReadOnlyPair
          label="Billing mode"
          value={
            charge.kind === 'MetroManilaFlat'
              ? 'Metro Manila flat rate'
              : 'Per-ton DOP rate'
          }
        />
        {charge.kind === 'MetroManilaFlat' && (
          <ReadOnlyPair label="Flat rate / truck" value={formatPhp(charge.flatRatePerTruck)} />
        )}
        {charge.kind === 'PerTonDopRate' && (
          <>
            <ReadOnlyPair label="Rate / ton" value={formatPhp(charge.ratePerTon)} />
            <ReadOnlyPair label="Discount / ton" value={formatPhp(charge.discountPerTon)} />
            <ReadOnlyPair label="Toll / truck" value={formatPhp(charge.tollPerTruck)} />
            {/* 14ykpqtvw4a — the floor each truck billed at. parseFreightCharge fills 40 / 20 for
                snapshots written before the choice existed, which is what they billed at. */}
            <ReadOnlyPair label="Min bill / truck" value={`${charge.minBillPerTruck} t`} />
            {/* 14ykpqtw3ua — only when the cap applied, i.e. it changed the truck count: the charge records
                maxLoadPerTruck only then (FreightAllocator.AppliedMaxLoadCap), null otherwise. */}
            {charge.maxLoadPerTruck != null && (
              <ReadOnlyPair label="Max load / truck" value={`${charge.maxLoadPerTruck} t`} />
            )}
          </>
        )}
        {/* Customer total = BilledAmount. DOP/MM rates are already tax-inclusive and the
            NetSuite line uses grossAmt — do NOT multiply by 1.12 here or it double-counts
            the VAT. Computed is shown alongside only when round-up actually moved the
            number, as an audit-transparency row for the rate-card-to-invoice gap. */}
        <ReadOnlyPair label="Total" value={formatPhp(charge.billedAmount)} />
        {charge.billedAmount !== charge.computedAmount && (
          <ReadOnlyPair
            label="Computed"
            value={formatPhp(charge.computedAmount)}
          />
        )}
      </div>
    </div>
  );
}

function FreightBanner({ tone, text }: { tone: 'amber' | 'muted'; text: string }) {
  const isAmber = tone === 'amber';
  return (
    <div
      className={cn(
        'flex items-center gap-2 border-y px-4 py-2.5 text-sm',
        isAmber
          ? 'bg-warning/10 text-warning'
          : 'bg-muted/30 text-muted-foreground',
      )}
    >
      <Truck className="h-4 w-4 shrink-0" />
      <span className={cn('font-medium', !isAmber && 'text-foreground')}>Freight & Delivery:</span>
      <span>{text}</span>
    </div>
  );
}

function truckTypeLabel(truck: string): string {
  if (truck === 'trailer') return 'Trailer';
  if (truck === '10wh') return '10-wheeler';
  return truck || '—';
}

/** Mirrors the `ReadOnlyPair` used by `ReadOnlySummaryBand` so the freight block
 *  visually merges with the customer block above it. */
function ReadOnlyPair({
  label,
  value,
  className,
}: {
  label: string;
  value: string | null | undefined;
  className?: string;
}) {
  const display = value && value.trim().length > 0 ? value : '—';
  return (
    <div className={cn('flex min-w-0 items-baseline gap-1.5', className)}>
      <span className="shrink-0 text-xs text-muted-foreground">{label}:</span>
      <span className="min-w-0 truncate font-medium" title={display}>
        {display}
      </span>
    </div>
  );
}
